import React, { useState, useMemo } from "react";
import { UploadCloud, CheckCircle2, X } from "lucide-react";
import { api } from "../services/api";
import { generateVoterPassword } from "../utils/voterAuth";
import { parseCsvText, autoMatchColumns, ColumnMapping } from "../utils/csvHelper";

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  electionId: string;
  electionTitle: string;
  onImportSuccess: (count: number) => void;
}

export const CsvImportModal: React.FC<CsvImportModalProps> = ({
  isOpen,
  onClose,
  electionId,
  electionTitle,
  onImportSuccess,
}) => {
  const [csvRawHeaders, setCsvRawHeaders] = useState<string[]>([]);
  const [csvRawRows, setCsvRawRows] = useState<string[][]>([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvImportMode, setCsvImportMode] = useState<"all" | "custom">("all");
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    matric: -1,
    name: -1,
    email: -1,
    password: -1,
    level: -1,
    gender: -1,
    hall: -1,
    department: -1,
  });
  const [autoGenPasswords, setAutoGenPasswords] = useState(true);
  const [importingVoters, setImportingVoters] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);

  const resetState = () => {
    setCsvRawHeaders([]);
    setCsvRawRows([]);
    setCsvFileName("");
    setCsvImportMode("all");
    setAutoGenPasswords(true);
    setImportProgress(null);
    setColumnMapping({
      matric: -1,
      name: -1,
      email: -1,
      password: -1,
      level: -1,
      gender: -1,
      hall: -1,
      department: -1,
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      const { headers, rows } = parseCsvText(text);
      if (headers.length === 0 || rows.length === 0) {
        alert("CSV file is empty or missing data rows");
        return;
      }

      const mapping = autoMatchColumns(headers);
      setCsvRawHeaders(headers);
      setCsvRawRows(rows);
      setColumnMapping(mapping);
      setCsvImportMode("all");
      setAutoGenPasswords(true);
    };
    reader.readAsText(file);
  };

  const csvParsedVoters = useMemo(() => {
    if (csvRawRows.length === 0) return [];
    return csvRawRows
      .map((row) => {
        const matric = columnMapping.matric !== -1 ? row[columnMapping.matric]?.trim() : "";
        const name = columnMapping.name !== -1 ? row[columnMapping.name]?.trim() : "";
        if (!matric || !name) return null;

        const email =
          columnMapping.email !== -1 && row[columnMapping.email]?.trim()
            ? row[columnMapping.email].trim()
            : `${matric}@student.ui.edu.ng`;

        let password = "";
        if (!autoGenPasswords && columnMapping.password !== -1 && row[columnMapping.password]?.trim()) {
          password = row[columnMapping.password].trim();
        } else {
          password = generateVoterPassword();
        }

        const rawLevel =
          columnMapping.level !== -1 && row[columnMapping.level]?.trim()
            ? row[columnMapping.level].trim().replace(/[^0-9]/g, "")
            : "100";
        const level = ["100", "200", "300", "400", "500"].includes(rawLevel) ? rawLevel : "100";

        const gender =
          columnMapping.gender !== -1 && row[columnMapping.gender]?.trim()
            ? row[columnMapping.gender].trim()
            : "Unspecified";

        const hall =
          columnMapping.hall !== -1 && row[columnMapping.hall]?.trim()
            ? row[columnMapping.hall].trim()
            : "Independence Hall";

        const department =
          columnMapping.department !== -1 && row[columnMapping.department]?.trim()
            ? row[columnMapping.department].trim()
            : "Faculty of Law";

        return {
          matric,
          name,
          email,
          password,
          level,
          gender,
          hall,
          department,
        };
      })
      .filter((v): v is NonNullable<typeof v> => v !== null);
  }, [csvRawRows, columnMapping, autoGenPasswords]);

  const handleCommitCsv = async () => {
    if (csvParsedVoters.length === 0) return;
    setImportingVoters(true);
    setImportProgress({ current: 0, total: csvParsedVoters.length });
    const res = await api.batchImportVoters(electionId, csvParsedVoters, (imported, total) => {
      setImportProgress({ current: imported, total });
    });
    if (res.success) {
      onImportSuccess(res.count);
      handleClose();
    } else {
      alert("Failed to import voters. Please check file format.");
    }
    setImportingVoters(false);
    setImportProgress(null);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "#171619",
          border: "1px solid #2D2C31",
          borderRadius: "14px",
          padding: "28px",
          width: "100%",
          maxWidth: "820px",
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            right: "20px",
            top: "20px",
            background: "transparent",
            border: "none",
            color: "#8E8E93",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>

        <h3 style={{ fontSize: "18px", fontWeight: 800, margin: "0 0 6px 0" }}>
          Batch Import Accredited Voters (CSV)
        </h3>
        <p style={{ color: "#8E8E93", fontSize: "13px", margin: "0 0 16px 0" }}>
          Upload your student voter registry into {electionTitle}. The system auto-matches columns or lets you pick attributes, and generates secure 7-digit access codes.
        </p>

        {csvRawRows.length === 0 ? (
          <div
            style={{
              border: "2px dashed #3F3F46",
              borderRadius: "12px",
              padding: "36px 20px",
              textAlign: "center",
              backgroundColor: "#0E0D0F",
            }}
          >
            <UploadCloud size={40} color="#FF9900" style={{ margin: "0 auto 12px auto" }} />
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#FFFFFF", marginBottom: "6px" }}>
              Select CSV File to Import
            </div>
            <div style={{ fontSize: "12px", color: "#8E8E93", marginBottom: "16px" }}>
              Upload student roster in CSV format. Columns like Matric, Name, Level, Hall, and Email will be automatically detected.
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFile}
              style={{ display: "none" }}
              id="csv-voter-input"
            />
            <label
              htmlFor="csv-voter-input"
              style={{
                display: "inline-block",
                padding: "8px 16px",
                borderRadius: "6px",
                backgroundColor: "#FF9900",
                color: "#171619",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Browse File
            </label>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* File status toolbar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#0E0D0F",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #2D2C31",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CheckCircle2 size={16} color="#10B981" />
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#10B981" }}>
                  Parsed {csvRawRows.length} rows from "{csvFileName}"
                </span>
                <span style={{ fontSize: "11.5px", color: "#8E8E93" }}>
                  ({csvRawHeaders.length} columns detected)
                </span>
              </div>
              <button
                onClick={resetState}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#EF4444",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Change File
              </button>
            </div>

            {/* Column Import Mode Selection */}
            <div
              style={{
                backgroundColor: "#1E1D21",
                padding: "14px",
                borderRadius: "10px",
                border: "1px solid #2D2C31",
              }}
            >
              <div style={{ fontSize: "12.5px", fontWeight: 700, color: "#E4E4E7", marginBottom: "10px" }}>
                How would you like to import columns?
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setCsvImportMode("all")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: csvImportMode === "all" ? "1.5px solid #FF9900" : "1px solid #2D2C31",
                    backgroundColor: csvImportMode === "all" ? "rgba(255, 153, 0, 0.1)" : "#0E0D0F",
                    color: csvImportMode === "all" ? "#FF9900" : "#A1A1AA",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>
                    Import All Matched Columns
                  </div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Automatically map detected columns (Matric, Name, Level, Hall, Gender, etc.)
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setCsvImportMode("custom")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: csvImportMode === "custom" ? "1.5px solid #FF9900" : "1px solid #2D2C31",
                    backgroundColor: csvImportMode === "custom" ? "rgba(255, 153, 0, 0.1)" : "#0E0D0F",
                    color: csvImportMode === "custom" ? "#FF9900" : "#A1A1AA",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>
                    Choose & Map Specific Columns
                  </div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Customize which attributes to import and specify exact source columns
                  </div>
                </button>
              </div>
            </div>

            {/* Custom Mapping Controls vs Auto-matched summary */}
            {csvImportMode === "custom" ? (
              <div
                style={{
                  backgroundColor: "#0E0D0F",
                  padding: "16px",
                  borderRadius: "10px",
                  border: "1px solid #2D2C31",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#A1A1AA",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "12px",
                  }}
                >
                  Column Attribute Mapping
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
                  {/* Matric */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Matric Number *
                    </label>
                    <select
                      value={columnMapping.matric}
                      onChange={(e) => setColumnMapping({ ...columnMapping, matric: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Selected --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Student Full Name *
                    </label>
                    <select
                      value={columnMapping.name}
                      onChange={(e) => setColumnMapping({ ...columnMapping, name: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Selected --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Email Address
                    </label>
                    <select
                      value={columnMapping.email}
                      onChange={(e) => setColumnMapping({ ...columnMapping, email: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Auto-generate (@student.ui.edu.ng) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Password */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Account Password
                    </label>
                    <select
                      value={columnMapping.password}
                      onChange={(e) => setColumnMapping({ ...columnMapping, password: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Autopopulate (7-digit Mixed Random) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Level */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Academic Level
                    </label>
                    <select
                      value={columnMapping.level}
                      onChange={(e) => setColumnMapping({ ...columnMapping, level: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Default (100L) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Gender */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Gender
                    </label>
                    <select
                      value={columnMapping.gender}
                      onChange={(e) => setColumnMapping({ ...columnMapping, gender: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Default (Unspecified) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Hall */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Hall of Residence
                    </label>
                    <select
                      value={columnMapping.hall}
                      onChange={(e) => setColumnMapping({ ...columnMapping, hall: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Default (Independence Hall) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Department */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Department / Faculty
                    </label>
                    <select
                      value={columnMapping.department}
                      onChange={(e) => setColumnMapping({ ...columnMapping, department: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Default (Faculty of Law) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#0E0D0F",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: "1px solid #2D2C31",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Auto-Detected Column Matches
                  </span>
                  <button
                    type="button"
                    onClick={() => setCsvImportMode("custom")}
                    style={{ background: "transparent", border: "none", color: "#FF9900", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                  >
                    Customize Specific Columns →
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: columnMapping.matric !== -1 ? "#10B981" : "#EF4444" }}>
                    Matric: <b>{columnMapping.matric !== -1 ? csvRawHeaders[columnMapping.matric] : "Missing"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: columnMapping.name !== -1 ? "#10B981" : "#EF4444" }}>
                    Name: <b>{columnMapping.name !== -1 ? csvRawHeaders[columnMapping.name] : "Missing"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Email: <b>{columnMapping.email !== -1 ? csvRawHeaders[columnMapping.email] : "Auto-derive"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Level: <b>{columnMapping.level !== -1 ? csvRawHeaders[columnMapping.level] : "Default (100L)"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Gender: <b>{columnMapping.gender !== -1 ? csvRawHeaders[columnMapping.gender] : "Default"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Hall: <b>{columnMapping.hall !== -1 ? csvRawHeaders[columnMapping.hall] : "Default"}</b>
                  </span>
                </div>
              </div>
            )}

            {/* Password Autopopulation Callout & Checkbox */}
            <div
              style={{
                backgroundColor: "rgba(255, 153, 0, 0.08)",
                border: "1px solid rgba(255, 153, 0, 0.25)",
                borderRadius: "10px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <input
                type="checkbox"
                id="autogen-pwd-toggle"
                checked={autoGenPasswords}
                onChange={(e) => setAutoGenPasswords(e.target.checked)}
                style={{ marginTop: "3px", width: "16px", height: "16px", accentColor: "#FF9900", cursor: "pointer" }}
              />
              <label htmlFor="autogen-pwd-toggle" style={{ fontSize: "12.5px", cursor: "pointer" }}>
                <span style={{ fontWeight: 700, color: "#FF9900", display: "block", marginBottom: "2px" }}>
                  Autopopulate Randomised 7-Digit Passwords
                </span>
                <span style={{ color: "#D4D4D8", lineHeight: 1.4 }}>
                  Generates secure 7-digit mixed uppercase & number passwords (excluding look-alikes like 0/O, 1/I/L) for all imported accounts.
                </span>
              </label>
            </div>

            {/* Live Preview Table */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#8E8E93", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Preview of Accredited Voters (Showing {Math.min(5, csvParsedVoters.length)} of {csvParsedVoters.length})
                </span>
                {columnMapping.matric === -1 || columnMapping.name === -1 ? (
                  <span style={{ fontSize: "11.5px", color: "#EF4444", fontWeight: 600 }}>
                    ⚠️ Matric Number and Name must be mapped
                  </span>
                ) : null}
              </div>

              <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #2D2C31", borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1E1D21", color: "#8E8E93", textAlign: "left" }}>
                      <th style={{ padding: "8px 12px" }}>Matric</th>
                      <th style={{ padding: "8px 12px" }}>Name</th>
                      <th style={{ padding: "8px 12px" }}>Password</th>
                      <th style={{ padding: "8px 12px" }}>Level</th>
                      <th style={{ padding: "8px 12px" }}>Gender</th>
                      <th style={{ padding: "8px 12px" }}>Hall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvParsedVoters.slice(0, 5).map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #222126" }}>
                        <td style={{ padding: "8px 12px", color: "#FF9900", fontWeight: 600 }}>{row.matric}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{row.name}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "#F4F4F5",
                              backgroundColor: "#232227",
                              border: "1px solid #3A3940",
                              padding: "2px 6px",
                              borderRadius: "4px",
                            }}
                          >
                            {row.password}
                          </span>
                        </td>
                        <td style={{ padding: "8px 12px" }}>{row.level}L</td>
                        <td style={{ padding: "8px 12px", color: "#8E8E93" }}>{row.gender}</td>
                        <td style={{ padding: "8px 12px", color: "#8E8E93" }}>{row.hall}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Commit Button */}
            <button
              disabled={importingVoters || csvParsedVoters.length === 0}
              onClick={handleCommitCsv}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "6px",
                backgroundColor: csvParsedVoters.length === 0 ? "#3F3F46" : "#FF9900",
                color: csvParsedVoters.length === 0 ? "#8E8E93" : "#171619",
                border: "none",
                fontWeight: 800,
                fontSize: "13.5px",
                cursor: csvParsedVoters.length === 0 ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {importingVoters
                ? (importProgress && importProgress.total > 0
                    ? `Accrediting Voters into Database (${importProgress.current} of ${importProgress.total})...`
                    : "Accrediting Voters into Database...")
                : `Commit & Accredit All ${csvParsedVoters.length} Voters`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
